import { CHARGE_TYPE_LABELS, type ChargeType, type OrderFeeTier, type ServiceTariff } from './billing';
import type { IsoDate } from './common';
import { RATE_CARD_KINDS, tierProblems, type RateCard, type RateCardKind, type RateTier } from './rates';

// The list price as the website (prontodepo.com.tr, pricing-config.ts) states it, and how the WMS
// charges it: the graduated order fee by the order's number in the month, storage per m³ and per
// pallet position per month charged day by day, a fee per box the warehouse provides.

/** Billing days and months are Türkiye's: a charge's service date, a month's order count, the end of a storage day. */
export const BILLING_TIME_ZONE = 'Europe/Istanbul';

/** desi = cm³ / 3000, so one cubic metre holds 333.33… desi */
export const DESI_PER_CUBIC_METRE = 1_000_000 / 3000;

/**
 * The desi a ₺ / m³ price is divided by, as the price list and every storage charge say it ("÷ 333,33
 * desi"): a product of 1 m³ measures 333.33 desi, and a month of it costs the m³ price to the kuruş.
 */
export const DESI_PER_CUBIC_METRE_BILLED = '333.33';

export const ORDER_FEE_TIERS_MAX = 10;

const MONEY = /^\d+(\.\d{1,2})?$/;
const RATE = /^\d+(\.\d{1,4})?$/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

/** The fixed fees as given to the server (decimal strings): by the price editors and by `pricing:set-list`. */
export interface TariffFields {
  readonly currency?: string;
  readonly orderFee?: string;
  /** graduated order fee; empty or missing: orderFee for every order */
  readonly orderFeeTiers?: readonly OrderFeeTier[];
  readonly extraUnitFee?: string;
  readonly receivingUnitFee?: string;
  /** ₺ / desi / gün (older tariffs); 0 when storageM3MonthFee is given */
  readonly storageDesiDayFee?: string;
  /** ₺ / m³ / ay for stock on shelves; null or missing: storageDesiDayFee */
  readonly storageM3MonthFee?: string | null;
  /** ₺ / palet yeri / ay; 0 or missing: stock in pallet bins is charged by desi like the rest */
  readonly palletMonthFee?: string;
  /** ₺ / koli the warehouse provides */
  readonly boxFee?: string;
  readonly defaultUnitDesi?: string;
  readonly vatRate?: string;
  readonly returnFee?: string;
}

/** A barem of a price list as the editors and the server command give it. */
export interface PriceListCard {
  readonly kind: RateCardKind;
  readonly carrierCode: string | null;
  readonly name: string;
  readonly extraDesiPrice: string | null;
  readonly tiers: readonly RateTier[];
}

/** One start date of the list price: from it until the next one starts (validTo), and whether a charge was priced by it. */
export interface PriceListVersion {
  readonly validFrom: IsoDate;
  readonly validTo: IsoDate | null;
  readonly used: boolean;
}

/** GET /api/admin/pricing/list and `pricing:list`: the list price (tenant_id NULL) in force on a day. */
export interface ListPricing {
  readonly on: IsoDate;
  readonly rateCards: readonly RateCard[];
  readonly tariff: ServiceTariff | null;
  /** newest first */
  readonly versions: readonly PriceListVersion[];
  /** list price versions that start after `on` */
  readonly upcomingChanges: number;
  /** sellers with prices of their own on `on` (codes): their own barem or fixed fees replace the list price's */
  readonly customSellers: readonly string[];
}

// --- the graduated order fee -------------------------------------------------------------------

/** What is wrong with a list of order fee steps (empty is fine: one price for every order). */
export function orderFeeTierProblems(tiers: readonly OrderFeeTier[]): string[] {
  if (tiers.length === 0) return [];
  const problems: string[] = [];
  if (tiers.length > ORDER_FEE_TIERS_MAX) problems.push(`En fazla ${ORDER_FEE_TIERS_MAX} kademe olabilir`);
  let previous = 0;
  tiers.forEach((t, i) => {
    if (!MONEY.test(t.price)) problems.push(`${i + 1}. kademe: geçersiz fiyat ${t.price}`);
    if (i === tiers.length - 1) {
      if (t.upTo !== null) problems.push('Son kademenin üst sınırı olmaz (“ve sonrası”)');
      return;
    }
    if (t.upTo === null || !Number.isInteger(t.upTo) || t.upTo < 1) {
      problems.push(`${i + 1}. kademenin üst sınırı tam sayı olmalı`);
      return;
    }
    if (t.upTo <= previous) problems.push(`${i + 1}. kademenin üst sınırı bir öncekinden büyük olmalı`);
    previous = t.upTo;
  });
  return problems;
}

export interface OrderFeeStep {
  readonly price: string;
  /** index of the step; null when the tariff has one price for every order */
  readonly step: number | null;
  /** first and last order number of the step (null: no end) */
  readonly from: number;
  readonly upTo: number | null;
}

/** The fee of the seller's nth handled order of a month (n from 1). */
export function orderFeeAt(tariff: Pick<ServiceTariff, 'orderFee' | 'orderFeeTiers'>, rank: number): OrderFeeStep {
  const tiers = tariff.orderFeeTiers;
  if (tiers.length === 0) return { price: tariff.orderFee, step: null, from: 1, upTo: null };
  let from = 1;
  for (const [i, t] of tiers.entries()) {
    if (t.upTo === null || rank <= t.upTo) return { price: t.price, step: i, from, upTo: t.upTo };
    from = t.upTo + 1;
  }
  // a valid list ends with an open step; a broken one bills its last step
  const last = tiers[tiers.length - 1]!;
  return { price: last.price, step: tiers.length - 1, from, upTo: last.upTo };
}

// --- days and months ----------------------------------------------------------------------------

/** The calendar day of a moment in Türkiye (or another zone), "2026-10-25". */
export function localDay(at: Date, timeZone: string = BILLING_TIME_ZONE): IsoDate {
  return new Intl.DateTimeFormat('en-CA', { timeZone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(at);
}

/** Days of the month a date is in (28–31). */
export function monthDays(day: IsoDate): number {
  return new Date(Date.UTC(Number(day.slice(0, 4)), Number(day.slice(5, 7)), 0)).getUTCDate();
}

/** "2026-10-25" → "2026-10-24" */
export function previousDay(day: IsoDate): IsoDate {
  return addDays(day, -1);
}

export function addDays(day: IsoDate, days: number): IsoDate {
  const d = new Date(`${day}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export const isIsoDate = (value: string): boolean => ISO_DATE.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) && new Date(`${value}T00:00:00Z`).toISOString().startsWith(value);

// --- storage by the month, charged by the day --------------------------------------------------

/** ₺ / desi / day of a ₺ / m³ / month price in a month of `days` days (349.90 ÷ 333.33 ÷ 31 = 0.033861… in October). */
export function desiDayRate(perCubicMetreMonth: string, days: number): number {
  return Number(perCubicMetreMonth) / (Number(DESI_PER_CUBIC_METRE_BILLED) * days);
}

/** ₺ / pallet position / day of a ₺ / pallet / month price in a month of `days` days. */
export function palletDayRate(perPalletMonth: string, days: number): number {
  return Number(perPalletMonth) / days;
}

/**
 * Boxes the warehouse provided for an order: every parcel but the units that leave in their own
 * packaging (products "Paketlemeden gönderilir", one parcel each); at least one when the order has
 * other goods, none when it has only such units.
 */
export function boxedParcels(parcels: number, units: { readonly own: number; readonly boxed: number }): number {
  if (units.boxed <= 0) return 0;
  return Math.max(1, parcels - units.own);
}

// --- Turkish texts ------------------------------------------------------------------------------

const MONEY_TR = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const RATE_TR = new Intl.NumberFormat('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const NUMBER_TR = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 });
const QUANTITY_TR = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 3 });
const MONTH_TR = new Intl.DateTimeFormat('tr-TR', { month: 'long', year: 'numeric', timeZone: 'UTC' });

/** "29.9" → "29,90" */
export const trMoney = (value: string | number): string => MONEY_TR.format(Number(value));
/** "0.033861" → "0,0339" (up to 4 decimals) */
export const trRate = (value: string | number): string => RATE_TR.format(Number(value));
/** "1000" → "1.000", "12.5" → "12,5" */
export const trNumber = (value: string | number): string => NUMBER_TR.format(Number(value));
/** "1.571" → "1,571", "2.000" → "2" (desi and pallet shares, up to 3 decimals) */
export const trQuantity = (value: string | number): string => QUANTITY_TR.format(Number(value));
/** "2026-10-25" → "Ekim 2026" */
export const trMonth = (day: IsoDate): string => MONTH_TR.format(new Date(`${day.slice(0, 7)}-01T00:00:00Z`));
const trDay = (day: IsoDate) => `${day.slice(8, 10)}.${day.slice(5, 7)}.${day.slice(0, 4)}`;

/** "1–250. sipariş", "251–1.000. sipariş", "1.001. sipariş ve sonrası" */
export function orderFeeStepText(from: number, upTo: number | null): string {
  if (upTo === null) return from === 1 ? 'her sipariş' : `${trNumber(from)}. sipariş ve sonrası`;
  return `${trNumber(from)}–${trNumber(upTo)}. sipariş`;
}

/** The steps with their order numbers: [{ text: "1–250. sipariş", price: "29.90" }, …] */
export function orderFeeSteps(tiers: readonly OrderFeeTier[]): { text: string; price: string }[] {
  let from = 1;
  return tiers.map((t) => {
    const step = { text: orderFeeStepText(from, t.upTo), price: t.price };
    if (t.upTo !== null) from = t.upTo + 1;
    return step;
  });
}

export type TariffLineKey = 'order' | 'extra' | 'storage' | 'pallet' | 'box' | 'returns' | 'receiving' | 'desi' | 'vat';

export interface TariffLine {
  readonly key: TariffLineKey;
  readonly label: string;
  readonly value: string;
  /** the graduated order fee's steps ("1–250. sipariş", price) */
  readonly steps?: readonly { readonly text: string; readonly price: string }[];
  /** how it is charged, when the value alone does not say */
  readonly note?: string;
}

/**
 * The fixed fees in words, as the panels and the server command show them. Monthly storage prices
 * are shown per month, with what a day of `on`'s month costs.
 */
export function tariffLines(t: ServiceTariff, on: IsoDate): TariffLine[] {
  const days = monthDays(on);
  const month = `${trMonth(on)}, ${days} gün`;
  const pallets = Number(t.palletMonthFee) > 0;
  const lines: TariffLine[] = [];
  if (t.orderFeeTiers.length > 0) {
    const steps = orderFeeSteps(t.orderFeeTiers);
    lines.push({
      key: 'order',
      label: 'Sipariş başı (toplama & paketleme)',
      value: steps.map((s) => `${s.text}: ${trMoney(s.price)} ₺`).join(' · '),
      steps,
      note: 'Kademeli: satıcının bir ayda (Türkiye saatiyle) paketlenen siparişleri sırayla sayılır; her sipariş düştüğü kademenin fiyatıyla ücretlenir.',
    });
  } else {
    lines.push({ key: 'order', label: 'Sipariş başı (toplama & paketleme)', value: `${trMoney(t.orderFee)} ₺ / sipariş` });
  }
  lines.push({ key: 'extra', label: 'Ek ürün', value: `${trMoney(t.extraUnitFee)} ₺ / adet`, note: 'Siparişteki ilk üründen sonraki her adet.' });
  const storageLabel = pallets ? 'Depolama (raf / koli)' : 'Depolama';
  if (t.storageM3MonthFee !== null) {
    lines.push({
      key: 'storage',
      label: storageLabel,
      value: `${trMoney(t.storageM3MonthFee)} ₺ / m³ / ay`,
      note: `Her gün, günün sonundaki stokla işlenir: ${trMoney(t.storageM3MonthFee)} ÷ 333,33 desi ÷ ayın gün sayısı (${month}: ${trRate(desiDayRate(t.storageM3MonthFee, days))} ₺ / desi / gün). Her gün ayın o güne kadarki toplamı bir kez yuvarlanır, kuruş farkı birikmez: bütün ay duran 1 m³ (333,33 desi) = ${trMoney(t.storageM3MonthFee)} ₺.`,
    });
  } else {
    lines.push({
      key: 'storage',
      label: storageLabel,
      value: `${trRate(t.storageDesiDayFee)} ₺ / desi / gün`,
      note: `Her gün, günün sonundaki stokla işlenir. 30 günlük ayda 1 m³ (333,33 desi) = ${trMoney(Number(t.storageDesiDayFee) * DESI_PER_CUBIC_METRE * 30)} ₺.`,
    });
  }
  lines.push(
    pallets
      ? {
          key: 'pallet',
          label: 'Palet depolama',
          value: `${trMoney(t.palletMonthFee)} ₺ / palet / ay`,
          note: `Günün sonunda satıcının ürünü bulunan her palet gözü bir palet sayılır: ${trMoney(t.palletMonthFee)} ÷ ayın gün sayısı (${month}: ${trRate(palletDayRate(t.palletMonthFee, days))} ₺ / palet / gün); bütün ay duran bir palet = ${trMoney(t.palletMonthFee)} ₺. Birden çok satıcının ürünü bulunan palet gözü, ürünlerin orada kapladığı yere göre (ölçüsüz ürünler aşağıdaki gibi) paylaştırılır; palet toplamda bir kez ücretlenir. Palet gözündeki stoktan ayrıca desi ücreti alınmaz.`,
        }
      : { key: 'pallet', label: 'Palet depolama', value: 'Ayrı fiyat yok', note: 'Palet gözündeki stok da desiyle ücretlenir.' },
  );
  lines.push(
    Number(t.boxFee) > 0
      ? { key: 'box', label: 'Koli', value: `${trMoney(t.boxFee)} ₺ / koli`, note: 'Deponun kolisiyle çıkan her koli; kendi ambalajıyla giden (paketlemeden gönderilen) ürünlerden alınmaz.' }
      : { key: 'box', label: 'Koli', value: 'Alınmaz' },
  );
  lines.push({ key: 'returns', label: 'Müşteri iadesi', value: `${trMoney(t.returnFee)} ₺ / iade` });
  lines.push({ key: 'receiving', label: 'Mal kabul', value: Number(t.receivingUnitFee) > 0 ? `${trRate(t.receivingUnitFee)} ₺ / adet` : 'Alınmaz' });
  lines.push(
    t.storageM3MonthFee !== null
      ? {
          key: 'desi',
          label: 'Ölçülmemiş ürün',
          value: 'Bulunduğu göze göre',
          note: `Ölçüsü girilmemiş ürün, durduğu yerin hacmiyle ücretlenir: hacmi tanımlı bir raf ya da palet gözünde, gözdeki ölçülü ürünlerin (kendi desileriyle) bıraktığı yeri o gözün ölçüsüz ürünleriyle adet oranında paylaşır; göz yalnız onunsa gözün tamamı sayılır (bir palette 10 ürün bir palettir). Hacmi tanımlı olmayan bir yerde ya da göz ölçülü ürünlerle dolu göründüğünde adet başı ${trNumber(t.defaultUnitDesi)} desi sayılır.`,
        }
      : { key: 'desi', label: 'Ölçülmemiş ürün', value: `${trNumber(t.defaultUnitDesi)} desi / adet`, note: 'Ölçüsü girilmemiş ürünün depolamada sayılan desisi.' },
  );
  lines.push({ key: 'vat', label: 'KDV', value: `%${trNumber(t.vatRate)}`, note: 'Tutarlar KDV hariçtir; KDV aylık faturada eklenir.' });
  return lines;
}

// --- the website's price list (pricing-config.ts as JSON) ---------------------------------------

/** What the website's price list becomes in the WMS, or what is wrong with it. */
export interface PriceListFromWebsite {
  readonly validFrom: IsoDate | null;
  readonly tariff: TariffFields;
  readonly rateCards: readonly PriceListCard[];
  /** how a field was taken (or left out), in Turkish */
  readonly notes: readonly string[];
  readonly problems: readonly string[];
}

type Json = Record<string, unknown>;
const isObject = (v: unknown): v is Json => typeof v === 'object' && v !== null && !Array.isArray(v);

/** Website fields the WMS has no use for (the calculator's inputs) */
const CALCULATOR_ONLY = new Set(['inputs']);
const KNOWN = new Set([
  'currency',
  'vatRate',
  'validFrom',
  'pickPack',
  'extraItemFee',
  'storage',
  'returnFee',
  'packaging',
  'shipping',
  'receiving',
  'minimumMonthlyFee',
  // the WMS's own additions (pricing:list --json writes them so the file goes back unchanged)
  'defaultUnitDesi',
  'rateCards',
]);

/**
 * Reads the website's price list (src/config/pricing-config.ts of the website, written as JSON, or
 * what `pricing:list --json` prints): amounts in TRY without VAT, vatRate as a fraction (0.2) or a
 * percentage (20). Unit conversions are none: the monthly storage prices are kept as they are and
 * charged by the day with the month's real day count.
 */
export function priceListFromWebsite(input: unknown): PriceListFromWebsite {
  const problems: string[] = [];
  const notes: string[] = [];
  if (!isObject(input)) return { validFrom: null, tariff: {}, rateCards: [], notes, problems: ['Dosya bir JSON nesnesi olmalı ({ … })'] };

  const amount = (value: unknown, field: string, pattern = MONEY, digits = 2): string | null => {
    const text = typeof value === 'number' ? (Number.isFinite(value) ? String(value) : '') : typeof value === 'string' ? value.trim().replace(',', '.') : '';
    if (!/^\d+(\.\d+)?$/.test(text)) {
      problems.push(`${field}: sıfır ya da pozitif bir sayı olmalı (${JSON.stringify(value)})`);
      return null;
    }
    const [whole, fraction = ''] = text.split('.');
    const trimmed = fraction.replace(/0+$/, '');
    if (trimmed.length > digits) {
      problems.push(`${field}: en fazla ${digits} ondalık olabilir (${text})`);
      return null;
    }
    const out = `${BigInt(whole!).toString()}.${trimmed.padEnd(2, '0')}`;
    return pattern.test(out) ? out : (problems.push(`${field}: geçersiz tutar (${text})`), null);
  };

  for (const key of Object.keys(input)) {
    if (CALCULATOR_ONLY.has(key)) notes.push(`“${key}” (sitedeki hesaplayıcının ayarları) yok sayıldı.`);
    else if (!KNOWN.has(key)) notes.push(`Bilinmeyen alan yok sayıldı: “${key}”.`);
  }

  let currency = 'TRY';
  if (input.currency !== undefined) {
    if (typeof input.currency === 'string' && /^[A-Z]{3}$/.test(input.currency)) currency = input.currency;
    else problems.push(`currency: üç harfli para birimi olmalı (${JSON.stringify(input.currency)})`);
  }

  let validFrom: IsoDate | null = null;
  if (input.validFrom !== undefined && input.validFrom !== null) {
    if (typeof input.validFrom === 'string' && isIsoDate(input.validFrom)) validFrom = input.validFrom;
    else problems.push(`validFrom: YYYY-AA-GG biçiminde tarih olmalı (${JSON.stringify(input.validFrom)})`);
  }

  // KDV: the website writes a fraction (0.2); a percentage (20) is taken too
  let vatRate = '20';
  if (input.vatRate !== undefined) {
    const v = typeof input.vatRate === 'number' ? input.vatRate : Number.NaN;
    if (!Number.isFinite(v) || v < 0 || v > 100) problems.push(`vatRate: 0,2 gibi bir oran ya da 20 gibi bir yüzde olmalı (${JSON.stringify(input.vatRate)})`);
    else {
      const percent = v < 1 ? v * 100 : v;
      const rounded = Math.round(percent * 100) / 100;
      if (Math.abs(rounded - percent) > 1e-9) problems.push(`vatRate: en fazla iki ondalıklı yüzde olmalı (${v})`);
      vatRate = String(rounded);
      if (v < 1 && v > 0) notes.push(`KDV oranı ${trNumber(v)} → %${trNumber(rounded)}.`);
    }
  }

  // sipariş hazırlama: graduated steps
  let orderFee: string | undefined;
  let orderFeeTiers: OrderFeeTier[] = [];
  const pickPack = input.pickPack;
  if (!isObject(pickPack)) problems.push('pickPack: sipariş hazırlama ücreti (kademeler) yok');
  else {
    if (pickPack.mode !== undefined && pickPack.mode !== 'graduated') {
      problems.push(`pickPack.mode: yalnız 'graduated' (kademeli: her kademe kendi fiyatıyla) işlenir; '${String(pickPack.mode)}' desteklenmiyor`);
    }
    if (!Array.isArray(pickPack.tiers) || pickPack.tiers.length === 0) problems.push('pickPack.tiers: en az bir kademe olmalı');
    else {
      pickPack.tiers.forEach((t: unknown, i: number) => {
        if (!isObject(t)) {
          problems.push(`pickPack.tiers[${i}]: { upTo, price } olmalı`);
          return;
        }
        const price = amount(t.price, `pickPack.tiers[${i}].price`);
        const upTo = t.upTo === null || t.upTo === undefined ? null : typeof t.upTo === 'number' ? t.upTo : Number.NaN;
        if (price !== null) orderFeeTiers.push({ upTo, price });
      });
      for (const p of orderFeeTierProblems(orderFeeTiers)) problems.push(`pickPack.tiers: ${p}`);
      // one open step is one price for every order
      if (orderFeeTiers.length === 1 && orderFeeTiers[0]!.upTo === null) {
        orderFee = orderFeeTiers[0]!.price;
        orderFeeTiers = [];
      } else if (orderFeeTiers.length > 0) orderFee = orderFeeTiers[0]!.price;
    }
  }

  const extraUnitFee = input.extraItemFee === undefined ? '0.00' : amount(input.extraItemFee, 'extraItemFee');

  let storageM3MonthFee: string | null = null;
  let storageDesiDayFee = '0';
  let palletMonthFee = '0.00';
  const storage = input.storage;
  if (!isObject(storage)) problems.push('storage: depolama fiyatı yok');
  else {
    if (storage.perCubicMeterMonth !== undefined && storage.perCubicMeterMonth !== null) {
      storageM3MonthFee = amount(storage.perCubicMeterMonth, 'storage.perCubicMeterMonth');
      if (storage.perDesiDay !== undefined && storage.perDesiDay !== null) problems.push('storage: perCubicMeterMonth ile perDesiDay birlikte verilemez');
    } else if (storage.perDesiDay !== undefined && storage.perDesiDay !== null) {
      storageDesiDayFee = amount(storage.perDesiDay, 'storage.perDesiDay', RATE, 4) ?? '0';
    } else problems.push('storage.perCubicMeterMonth: raf / koli depolama fiyatı (₺ / m³ / ay) yok');
    if (storage.perPalletMonth !== undefined && storage.perPalletMonth !== null) palletMonthFee = amount(storage.perPalletMonth, 'storage.perPalletMonth') ?? '0.00';
  }

  const returnFee = input.returnFee === undefined ? '0.00' : amount(input.returnFee, 'returnFee');

  let boxFee = '0.00';
  const packaging = input.packaging;
  if (packaging !== undefined) {
    if (!isObject(packaging)) problems.push('packaging: { enabled, perBox } olmalı');
    else if (packaging.enabled === false) notes.push('Koli ücreti sitede kapalı (packaging.enabled: false): koli için ücret alınmaz.');
    else boxFee = amount(packaging.perBox ?? 0, 'packaging.perBox') ?? '0.00';
  }

  let receivingUnitFee = '0';
  const receiving = input.receiving;
  if (receiving !== undefined) {
    if (!isObject(receiving)) problems.push('receiving: { enabled, perUnit } olmalı');
    else if (receiving.enabled === true) receivingUnitFee = amount(receiving.perUnit ?? 0, 'receiving.perUnit', RATE, 4) ?? '0';
    else if (receiving.perUnit !== undefined) {
      notes.push(`Mal kabul sitede kapalı (receiving.enabled: false): ücret alınmaz (açılırsa ${trRate(Number(receiving.perUnit))} ₺ / adet).`);
    }
  }

  const rateCards: PriceListCard[] = [];
  const shipping = input.shipping;
  if (shipping !== undefined) {
    if (!isObject(shipping)) problems.push('shipping: { enabled, perParcel } olmalı');
    else if (shipping.enabled === true) {
      const perParcel = amount(shipping.perParcel ?? 0, 'shipping.perParcel');
      if (perParcel !== null && Number(perParcel) > 0) {
        rateCards.push({ kind: 'shipping', carrierCode: null, name: 'Kargo (gönderi başı)', extraDesiPrice: null, tiers: [{ minDesi: 0, maxDesi: 10_000, unitPrice: perParcel }] });
        notes.push(`Kargo: koli başına ${trMoney(perParcel)} ₺ (her kargo firması, her desi; yalnız deponun anlaşmasıyla gönderilen siparişlerde).`);
      }
    } else notes.push('Kargo sitede gösterilmiyor (shipping.enabled: false): liste fiyatında kargo baremi yok, kargo ayrıca faturalanır.');
  }
  if (input.rateCards !== undefined) {
    if (!Array.isArray(input.rateCards)) problems.push('rateCards: dizi olmalı');
    else {
      input.rateCards.forEach((c: unknown, i: number) => {
        const card = priceListCard(c, `rateCards[${i}]`, problems);
        if (card) rateCards.push(card);
      });
    }
  }
  const keys = new Set<string>();
  for (const c of rateCards) {
    const key = `${c.kind}|${c.carrierCode ?? ''}`;
    if (keys.has(key)) problems.push(`Aynı barem iki kez verilmiş: ${c.name}`);
    keys.add(key);
  }

  if (input.minimumMonthlyFee !== undefined && input.minimumMonthlyFee !== null && Number(input.minimumMonthlyFee) !== 0) {
    problems.push('minimumMonthlyFee: aylık asgari hizmet bedeli WMS’te yok; 0 olmalı');
  }

  let defaultUnitDesi: string | undefined;
  if (input.defaultUnitDesi !== undefined) {
    const v = amount(input.defaultUnitDesi, 'defaultUnitDesi');
    if (v !== null && Number(v) <= 0) problems.push('defaultUnitDesi: sıfırdan büyük olmalı');
    else if (v !== null) defaultUnitDesi = v;
  }

  return {
    validFrom,
    tariff: {
      currency,
      ...(orderFee !== undefined ? { orderFee } : {}),
      orderFeeTiers,
      extraUnitFee: extraUnitFee ?? '0.00',
      receivingUnitFee,
      storageDesiDayFee,
      storageM3MonthFee,
      palletMonthFee,
      boxFee,
      ...(defaultUnitDesi !== undefined ? { defaultUnitDesi } : {}),
      vatRate,
      returnFee: returnFee ?? '0.00',
    },
    rateCards,
    notes,
    problems,
  };
}

function priceListCard(c: unknown, field: string, problems: string[]): PriceListCard | null {
  if (!isObject(c)) {
    problems.push(`${field}: barem nesnesi olmalı`);
    return null;
  }
  const kind = c.kind;
  if (typeof kind !== 'string' || !(RATE_CARD_KINDS as readonly string[]).includes(kind)) {
    problems.push(`${field}.kind: 'shipping' ya da 'handling' olmalı`);
    return null;
  }
  const carrierCode = c.carrierCode === undefined || c.carrierCode === null ? null : String(c.carrierCode);
  if (carrierCode !== null && (kind === 'handling' || !/^[a-z][a-z0-9_]*$/.test(carrierCode))) problems.push(`${field}.carrierCode: geçersiz (${carrierCode})`);
  const name = typeof c.name === 'string' && c.name.trim() ? c.name.trim() : null;
  if (!name) problems.push(`${field}.name: barem adı yok`);
  const tiers: RateTier[] = Array.isArray(c.tiers)
    ? c.tiers.map((t: unknown) => (isObject(t) ? { minDesi: Number(t.minDesi), maxDesi: Number(t.maxDesi), unitPrice: String(t.unitPrice) } : { minDesi: Number.NaN, maxDesi: Number.NaN, unitPrice: '' }))
    : [];
  for (const p of tierProblems(tiers)) problems.push(`${field}: ${p}`);
  const extra = c.extraDesiPrice === undefined || c.extraDesiPrice === null ? null : String(c.extraDesiPrice);
  if (extra !== null && !MONEY.test(extra)) problems.push(`${field}.extraDesiPrice: geçersiz tutar (${extra})`);
  return name ? { kind: kind as RateCardKind, carrierCode, name, extraDesiPrice: extra, tiers } : null;
}

const num = (value: string) => Number(value);

/**
 * The list price in the website's format (what `pricing:list --json` prints): read back by
 * priceListFromWebsite unchanged. Barem tables the website has no field for go under rateCards.
 */
export function websitePriceList(p: ListPricing): Record<string, unknown> {
  const t = p.tariff;
  const flatShipping = p.rateCards.find((c) => c.kind === 'shipping' && c.carrierCode === null && c.tiers.length === 1 && c.tiers[0]!.minDesi === 0 && c.tiers[0]!.maxDesi >= 10_000 && c.extraDesiPrice === null);
  const otherCards = p.rateCards.filter((c) => c !== flatShipping);
  const validFrom = [t?.validFrom, ...p.rateCards.map((c) => c.validFrom)].filter((d): d is string => !!d).sort().at(-1) ?? null;
  return {
    currency: t?.currency ?? 'TRY',
    vatRate: t ? num(t.vatRate) / 100 : 0.2,
    validFrom,
    pickPack: {
      mode: 'graduated',
      tiers: t ? (t.orderFeeTiers.length > 0 ? t.orderFeeTiers.map((x) => ({ upTo: x.upTo, price: num(x.price) })) : [{ upTo: null, price: num(t.orderFee) }]) : [],
    },
    extraItemFee: t ? num(t.extraUnitFee) : 0,
    storage: t
      ? t.storageM3MonthFee !== null
        ? { perCubicMeterMonth: num(t.storageM3MonthFee), perPalletMonth: num(t.palletMonthFee) }
        : { perDesiDay: num(t.storageDesiDayFee), perPalletMonth: num(t.palletMonthFee) }
      : {},
    returnFee: t ? num(t.returnFee) : 0,
    packaging: { enabled: t ? num(t.boxFee) > 0 : false, perBox: t ? num(t.boxFee) : 0 },
    shipping: { enabled: flatShipping !== undefined, perParcel: flatShipping ? num(flatShipping.tiers[0]!.unitPrice) : 0 },
    receiving: { enabled: t ? num(t.receivingUnitFee) > 0 : false, perUnit: t ? num(t.receivingUnitFee) : 0 },
    minimumMonthlyFee: 0,
    defaultUnitDesi: t ? num(t.defaultUnitDesi) : 1,
    ...(otherCards.length > 0
      ? {
          rateCards: otherCards.map((c) => ({
            kind: c.kind,
            carrierCode: c.carrierCode,
            name: c.name,
            extraDesiPrice: c.extraDesiPrice,
            tiers: c.tiers.map((x) => ({ minDesi: x.minDesi, maxDesi: x.maxDesi, unitPrice: x.unitPrice })),
          })),
        }
      : {}),
  };
}

// --- the server command's texts -----------------------------------------------------------------

const pad = (text: string, width: number) => (text.length >= width ? `${text} ` : text.padEnd(width));

function cardLines(c: RateCard): string[] {
  const head = c.kind === 'shipping' ? `Kargo baremi${c.carrierCode ? ` (${c.carrierCode})` : ' (her kargo firması)'}` : 'Elleçleme baremi';
  const lines = [`  ${head}: ${c.name}`];
  for (const t of c.tiers) lines.push(`    ${pad(`${trNumber(t.minDesi)}–${trNumber(t.maxDesi)} desi`, 22)}${trMoney(t.unitPrice)} ₺ / koli`);
  if (c.extraDesiPrice) lines.push(`    ${pad('üstü, her desi', 22)}${trMoney(c.extraDesiPrice)} ₺`);
  return lines;
}

/** The price list as lines: the fixed fees, the barem tables. */
export function priceListLines(p: Pick<ListPricing, 'on' | 'rateCards' | 'tariff'>): string[] {
  const lines: string[] = [];
  if (!p.tariff) lines.push('  Sabit ücret yok (sipariş, depolama, koli, iade, mal kabul: hiçbiri ücretlenmez).');
  else {
    // the unmeasured products' rule is worth the words only where it is not a plain desi per unit
    const explained = new Set(['storage', 'pallet', 'box', ...(p.tariff.storageM3MonthFee !== null ? ['desi'] : [])]);
    for (const l of tariffLines(p.tariff, p.on)) {
      if (l.steps) {
        lines.push(`  ${l.label}, kademeli (bir ayın siparişleri sırayla sayılır):`);
        for (const s of l.steps) lines.push(`    ${pad(s.text, 30)}${trMoney(s.price)} ₺`);
        continue;
      }
      lines.push(`  ${pad(l.label, 34)}${l.value}`);
      if (l.note && explained.has(l.key) && !(l.key === 'pallet' && Number(p.tariff.palletMonthFee) === 0)) lines.push(`    ${l.note}`);
    }
  }
  if (p.rateCards.length === 0) lines.push('  Barem yok: elleçlemede desi baremi uygulanmaz, kargo liste fiyatında yok (ayrıca faturalanır).');
  for (const c of p.rateCards) lines.push(...cardLines(c));
  return lines;
}

/** `pricing:list`: the list price in force, its versions and the sellers with prices of their own. */
export function listPricingLines(p: ListPricing): string[] {
  const since = [p.tariff?.validFrom, ...p.rateCards.map((c) => c.validFrom)].filter((d): d is string => !!d).sort().at(-1);
  const lines = [
    p.tariff || p.rateCards.length > 0
      ? `Liste fiyatı, ${trDay(p.on)} günü geçerli olan${since ? ` (${trDay(since)} tarihinden beri)` : ''}; tutarlar KDV hariç:`
      : `${trDay(p.on)} günü geçerli liste fiyatı yok: liste fiyatındaki satıcılardan hiçbir ücret alınmıyor.`,
  ];
  if (p.tariff || p.rateCards.length > 0) lines.push(...priceListLines(p));
  if (p.versions.length > 0) {
    lines.push('', 'Sürümler (en yenisi üstte):');
    for (const v of p.versions) {
      const state = v.validFrom > p.on ? 'ileri tarihli' : v.used ? 'ücret işlendi' : 'henüz ücret işlenmedi';
      lines.push(`  ${trDay(v.validFrom)} – ${v.validTo ? trDay(v.validTo) : 'süresiz'}  (${state})`);
    }
  }
  lines.push('', p.customSellers.length === 0 ? 'Bütün satıcılar liste fiyatında.' : `Kendi fiyatı olan satıcılar (kendi baremleri / sabit ücretleri geçerli): ${p.customSellers.join(', ')}`);
  return lines;
}

/** What `pricing:set-list` did (or would do with --dry-run). */
export interface ListPriceChange {
  readonly validFrom: IsoDate;
  readonly today: IsoDate;
  readonly dryRun: boolean;
  readonly before: ListPricing;
  readonly after: ListPricing;
}

export function listPriceChangeLines(change: ListPriceChange, notes: readonly string[] = []): string[] {
  const { before, after } = change;
  const lines = [`Liste fiyatı ${trDay(change.validFrom)} tarihinden itibaren${change.dryRun ? ' (deneme: hiçbir şey kaydedilmedi)' : ''}:`, ...priceListLines(after)];
  if (notes.length > 0) lines.push('', ...notes);
  lines.push('');
  const old = before.tariff || before.rateCards.length > 0;
  if (!old) lines.push(`Önceki liste fiyatı: ${trDay(change.validFrom)} günü geçerli liste fiyatı yoktu.`);
  else lines.push(`Önceki liste fiyatı ${trDay(previousDay(change.validFrom))} günü sona ${change.dryRun ? 'erer' : 'erdi'}; o güne kadar işlenen ücretler olduğu gibi kalır.`);
  if (after.customSellers.length > 0) lines.push(`Kendi fiyatı olan satıcılar etkilenmez (kendi tanımladıkları kalemlerde): ${after.customSellers.join(', ')}`);
  if (change.validFrom < change.today) {
    lines.push(
      `Başlangıç geçmişte: ${trDay(change.validFrom)} ile bugün arasında kaydedilmemiş ücretler kendiliğinden işlenmez. Görmek ve işlemek için:`,
      `  billing:backfill --from ${change.validFrom} --dry-run`,
      `  billing:backfill --from ${change.validFrom}`,
    );
  }
  if (change.dryRun) lines.push('Kaydetmek için komutu --dry-run olmadan çalıştırın.');
  return lines;
}

/** `billing:backfill`: what was charged (or would be, with --dry-run), per seller and type. */
export interface BackfillReport {
  readonly from: IsoDate;
  readonly to: IsoDate;
  /** last day whose storage was looked at (yesterday at the latest); null: none */
  readonly storageTo: IsoDate | null;
  readonly tenantCode: string | null;
  readonly dryRun: boolean;
  readonly scanned: { readonly orders: number; readonly returns: number; readonly receipts: number; readonly storageDays: number };
  readonly charges: readonly { readonly tenantCode: string; readonly type: ChargeType; readonly count: number; readonly amount: string; readonly currency: string }[];
  /** new charges dated in a period that already has an invoice: they go on the next one */
  readonly intoInvoicedPeriods: readonly { readonly tenantCode: string; readonly count: number; readonly amount: string; readonly invoices: readonly string[] }[];
  /** days of the range without a price in force (the list price, or the seller's own when one is named) */
  readonly unpricedDays: readonly IsoDate[];
}

const sumAmounts = (amounts: readonly string[]) => {
  const minor = amounts.reduce((n, a) => {
    const [w, f = ''] = a.split('.');
    return n + BigInt(w!) * 100n + BigInt(f.padEnd(2, '0').slice(0, 2));
  }, 0n);
  return `${minor / 100n}.${(minor % 100n).toString().padStart(2, '0')}`;
};

/** Consecutive days as ranges: "01.10.2026–14.10.2026, 20.10.2026" */
function dayRanges(days: readonly IsoDate[]): string {
  const out: string[] = [];
  let start: IsoDate | null = null;
  let last: IsoDate | null = null;
  for (const d of [...days].sort()) {
    if (start && last && addDays(last, 1) === d) {
      last = d;
      continue;
    }
    if (start && last) out.push(start === last ? trDay(start) : `${trDay(start)}–${trDay(last)}`);
    start = d;
    last = d;
  }
  if (start && last) out.push(start === last ? trDay(start) : `${trDay(start)}–${trDay(last)}`);
  return out.join(', ');
}

export function backfillReportLines(r: BackfillReport): string[] {
  const lines = [
    `Geriye dönük ücret kaydı: ${trDay(r.from)} – ${trDay(r.to)}, ${r.tenantCode ? `${r.tenantCode} satıcısı` : 'bütün satıcılar'}${r.dryRun ? ' (deneme: hiçbir şey kaydedilmedi)' : ''}`,
    `Bakılan: ${trNumber(r.scanned.orders)} paketlenen sipariş, ${trNumber(r.scanned.returns)} kabul edilen iade, ${trNumber(r.scanned.receipts)} kapanan mal kabul, ${trNumber(r.scanned.storageDays)} günün depolaması${r.storageTo ? ` (${trDay(r.storageTo)} gününe kadar; bugünün depolaması gece işlenir)` : ''}.`,
    '',
  ];
  if (r.unpricedDays.length > 0) {
    lines.push(
      `Uyarı: şu günlerde ${r.tenantCode ? 'bu satıcının fiyatı da liste fiyatı da' : 'liste fiyatı'} yok, o günlerin işleri ücretlenmiyor: ${dayRanges(r.unpricedDays)}`,
      '  Önce fiyatı girin (pricing:set-list <dosya> --from <tarih>), sonra bu komutu yeniden çalıştırın.',
      '',
    );
  }
  if (r.charges.length === 0) lines.push(r.dryRun ? 'Kaydedilecek yeni ücret yok.' : 'Yeni ücret kaydedilmedi: hepsi zaten kayıtlı ya da fiyatı yok.');
  else {
    const sellers = [...new Set(r.charges.map((c) => c.tenantCode))].sort();
    for (const code of sellers) {
      const mine = r.charges.filter((c) => c.tenantCode === code);
      lines.push(code);
      for (const c of mine) lines.push(`  ${pad(CHARGE_TYPE_LABELS[c.type], 34)}${pad(`${trNumber(c.count)} kalem`, 12)}${trMoney(c.amount)} ${c.currency === 'TRY' ? '₺' : c.currency}`);
      lines.push(`  ${pad('Toplam (KDV hariç)', 46)}${trMoney(sumAmounts(mine.map((c) => c.amount)))} ₺`);
    }
    lines.push('', `Genel toplam (KDV hariç): ${trMoney(sumAmounts(r.charges.map((c) => c.amount)))} ₺ · ${trNumber(r.charges.reduce((n, c) => n + c.count, 0))} kalem ${r.dryRun ? 'kaydedilecek' : 'kaydedildi'}.`);
  }
  for (const x of r.intoInvoicedPeriods) {
    lines.push(
      `${x.tenantCode}: ${trNumber(x.count)} kalem (${trMoney(x.amount)} ₺) faturası kesilmiş bir döneme düşüyor (${x.invoices.join(', ')}); o faturaya dokunulmaz, bu kalemler satıcının bir sonraki faturasına girer.`,
    );
  }
  lines.push('Zaten kayıtlı ücretler atlandı, var olan hiçbir ücret ve fatura değiştirilmedi.');
  if (r.dryRun) lines.push('Kaydetmek için komutu --dry-run olmadan çalıştırın.');
  return lines;
}
